import React, { useState, useContext, useRef } from 'react';
import {
  Box, Button, Heading, VStack, useToast, Text, Icon, HStack,
  Alert, AlertIcon, AlertTitle, AlertDescription, List, ListItem
} from "@chakra-ui/react";
import { AttachmentIcon } from '@chakra-ui/icons';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

function ManualCPTUpload() {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setUploadResult(null);
      } else {
        toast({
          title: 'Invalid File Type',
          description: 'Please select a .csv file.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({ title: 'No file selected', status: 'warning', duration: 3000 });
      return;
    }

    setIsUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await callApi('/api/manual-coding/upload-csv', accessToken, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setUploadResult({ status: 'success', data });
        toast({
          title: 'Upload Successful',
          description: data.message,
          status: 'success',
          duration: 5000,
        });
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      setUploadResult({ status: 'error', message: error.message });
      toast({
        title: 'Upload Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8}>
        <VStack spacing={6} align="stretch" maxW="xl" mx="auto">
          <Heading>Upload Manual CPT Data</Heading>
          <Text>
            Upload a CSV file with the following columns: <strong>external_case_id</strong>, <strong>cpt</strong>, and <strong>notes</strong>.
          </Text>
          <Box p={6} borderWidth="1px" borderRadius="md" borderColor="gray.200">
            <VStack spacing={4}>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <Button
                leftIcon={<AttachmentIcon />}
                onClick={() => fileInputRef.current.click()}
                isDisabled={isUploading}
                w="full"
              >
                Choose CSV File
              </Button>
              {selectedFile && <Text>Selected file: {selectedFile.name}</Text>}
              <Button
                colorScheme="green"
                onClick={handleUpload}
                isDisabled={!selectedFile || isUploading}
                isLoading={isUploading}
                loadingText="Uploading..."
                w="full"
              >
                Upload and Process
              </Button>
            </VStack>
          </Box>

          {uploadResult && (
            <Alert
              status={uploadResult.status}
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="auto"
              p={4}
              borderRadius="md"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                {uploadResult.status === 'success' ? 'Processing Complete' : 'Processing Failed'}
              </AlertTitle>
              <AlertDescription maxWidth="sm">
                {uploadResult.status === 'success' ? (
                  <Box>
                    <Text>Successfully processed rows: {uploadResult.data.success_count}</Text>
                    <Text>Failed rows: {uploadResult.data.error_count}</Text>
                    {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                      <Box mt={4} textAlign="left">
                        <Text fontWeight="bold">Error Details:</Text>
                        <List spacing={2} maxHeight="200px" overflowY="auto" p={2} bg="gray.100" borderRadius="md">
                          {uploadResult.data.errors.map((err, index) => (
                            <ListItem key={index} fontSize="sm">{err}</ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Text>{uploadResult.message}</Text>
                )}
              </AlertDescription>
            </Alert>
          )}
        </VStack>
      </Box>
    </Box>
  );
}

export default ManualCPTUpload;